import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NotificationService } from '@progress/kendo-angular-notification';
import { DialogService } from '@progress/kendo-angular-dialog';
import { NotificationAdminService, EmailTemplate } from '../../../shared/services/notification-admin.service';
import { EditorComponent } from '@progress/kendo-angular-editor';

interface ButtonConfig {
  text: string;
  url: string;
  style: 'primary' | 'secondary' | 'success' | 'warning' | 'danger';
}

interface VariableCategory {
  name: string;
  variables: string[];
  expanded: boolean;
}

@Component({
    selector: 'app-email-templates',
    templateUrl: './email-templates.component.html',
    styleUrls: ['./email-templates.component.css'],
    standalone: false
})
export class EmailTemplatesComponent implements OnInit {
  @ViewChild('bodyEditor') bodyEditor!: EditorComponent;

  templates: EmailTemplate[] = [];
  eventTypes: string[] = [];
  loading = false;
  selectedTemplate: EmailTemplate | null = null;
  isEditing = false;
  isNew = false;

  templateForm: FormGroup;
  previewHtml = '';
  showPreview = false;
  testEmailAddress = '';

  // Template Builder state
  showButtonBuilder = false;
  showVariablePanel = false;
  variableSearchText = '';
  showLivePreview = false;
  livePreviewHtml = '';
  buttonConfig: ButtonConfig = {
    text: '',
    url: '',
    style: 'primary'
  };

  buttonStyles: { value: 'primary' | 'secondary' | 'success' | 'warning' | 'danger'; label: string; color: string }[] = [
    { value: 'primary', label: 'Primary (Blue)', color: '#1d4ed8' },
    { value: 'secondary', label: 'Secondary (Gray)', color: '#475569' },
    { value: 'success', label: 'Success (Green)', color: '#0f766e' },
    { value: 'warning', label: 'Warning (Orange)', color: '#b45309' },
    { value: 'danger', label: 'Danger (Red)', color: '#b42318' }
  ];

  availableVariables: { [key: string]: string[] } = {
    Common: ['UserName', 'UserEmail', 'CurrentDate', 'AppUrl'],
    OrderCreated: ['OrderId', 'OrderType', 'Reference', 'Priority', 'Requestor', 'RequiredDate'],
    OrderSubmitted: ['OrderId', 'OrderType', 'Reference', 'Priority', 'Requestor', 'SubmittedBy', 'SubmittedDate'],
    OrderApproved: ['OrderId', 'ApprovedBy', 'ApprovalDate', 'Comments'],
    OrderRejected: ['OrderId', 'RejectedBy', 'RejectionDate', 'Comments'],
    OrderCompleted: ['OrderId', 'CompletedDate'],
    TaskAssigned: ['TaskId', 'TaskName', 'AssignedTo', 'DueDate', 'Priority'],
    TaskPickedUp: ['TaskId', 'TaskName', 'PickedUpBy', 'PickupTime'],
    TaskStatusChanged: ['TaskId', 'TaskName', 'OldStatus', 'NewStatus'],
    CutterOverrideRequested: ['OrderId', 'RequestedBy', 'CutterFamily', 'Justification', 'ApproveUrl', 'DenyUrl'],
    CutterOverrideApproved: ['OrderId', 'ApprovedBy', 'ApprovalCode', 'ExpirationDate'],
    CutterOverrideDenied: ['OrderId', 'DeniedBy', 'Reason'],
    FileUploaded: ['OrderId', 'FileName', 'UploadedBy'],
    CommentAdded: ['OrderId', 'CommentBy', 'CommentText'],
    PriorityChanged: ['OrderId', 'OldPriority', 'NewPriority', 'ChangedBy']
  };

  variableCategories: VariableCategory[] = [];

  constructor(
    private notificationAdminService: NotificationAdminService,
    private notificationService: NotificationService,
    private dialogService: DialogService,
    private fb: FormBuilder,
    private router: Router
  ) {
    this.templateForm = this.fb.group({
      id: [null],
      name: ['', [Validators.required, Validators.maxLength(100)]],
      subject: ['', [Validators.required, Validators.maxLength(500)]],
      body: ['', Validators.required],
      eventType: ['', Validators.required],
      isActive: [true]
    });
  }

  ngOnInit(): void {
    this.loadTemplates();
    this.loadEventTypes();
    this.buildVariableCategories();
    
    // Subscribe to body changes for live preview
    this.templateForm.get('body')?.valueChanges.subscribe(() => {
      if (this.showLivePreview) {
        this.updateLivePreview();
      }
    });
  }

  toggleLivePreview(): void {
    this.showLivePreview = !this.showLivePreview;
    if (this.showLivePreview) {
      this.updateLivePreview();
    }
  }

  updateLivePreview(): void {
    const body = this.templateForm.get('body')?.value || '';
    const eventType = this.templateForm.get('eventType')?.value || '';
    const sampleVariables = this.getSampleVariables(eventType);
    
    // Replace all variables with sample values
    let preview = body;
    Object.keys(sampleVariables).forEach(key => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'gi');
      preview = preview.replace(regex, `<span class="variable-rendered">${sampleVariables[key]}</span>`);
    });
    
    // Wrap in email container styling
    this.livePreviewHtml = this.wrapInEmailTemplate(preview);
  }

  private wrapInEmailTemplate(content: string): string {
    return `
      <div style="font-family: 'Segoe UI', Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff; padding: 24px;">
        ${content}
      </div>
    `;
  }

  getRenderedSubject(): string {
    const subject = this.templateForm.get('subject')?.value || '';
    const eventType = this.templateForm.get('eventType')?.value || '';
    const sampleVariables = this.getSampleVariables(eventType);
    
    let rendered = subject;
    Object.keys(sampleVariables).forEach(key => {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'gi');
      rendered = rendered.replace(regex, `<span class="variable-rendered">${sampleVariables[key]}</span>`);
    });
    
    return rendered;
  }

  buildVariableCategories(): void {
    this.variableCategories = [
      { name: 'Common', variables: this.availableVariables['Common'], expanded: true }
    ];
  }

  updateVariableCategoriesForEventType(eventType: string): void {
    const categories: VariableCategory[] = [
      { name: 'Common', variables: this.availableVariables['Common'] || [], expanded: true }
    ];

    if (eventType && this.availableVariables[eventType]) {
      categories.push({
        name: eventType,
        variables: this.availableVariables[eventType],
        expanded: true
      });
    }

    this.variableCategories = categories;
  }

  get filteredVariableCategories(): VariableCategory[] {
    if (!this.variableSearchText.trim()) {
      return this.variableCategories;
    }

    const search = this.variableSearchText.toLowerCase();
    return this.variableCategories
      .map(cat => ({
        ...cat,
        variables: cat.variables.filter(v => v.toLowerCase().includes(search)),
        expanded: true
      }))
      .filter(cat => cat.variables.length > 0);
  }

  openDesigner(templateId?: number): void {
    if (templateId) {
      this.router.navigate(['/admin/email-designer', templateId]);
    } else {
      this.router.navigate(['/admin/email-designer']);
    }
  }

  loadTemplates(): void {
    this.loading = true;
    this.notificationAdminService.getTemplates().subscribe({
      next: (templates) => {
        this.templates = templates;
        this.loading = false;
      },
      error: (err) => {
        this.showNotification('Error loading templates', 'error');
        this.loading = false;
      }
    });
  }

  loadEventTypes(): void {
    this.notificationAdminService.getEventTypes().subscribe({
      next: (types) => {
        this.eventTypes = types;
      },
      error: (err) => {
        console.error('Error loading event types', err);
      }
    });
  }

  onSelectionChange(event: any): void {
    if (event.selectedRows && event.selectedRows.length > 0 && event.selectedRows[0]) {
      this.selectTemplate(event.selectedRows[0].dataItem);
    }
  }

  selectTemplate(template: EmailTemplate | undefined): void {
    if (!template) return;
    this.selectedTemplate = template;
    this.isEditing = false;
    this.isNew = false;
    this.showPreview = false;
    this.closeBuilderPanels();
  }

  createNew(): void {
    this.selectedTemplate = null;
    this.isNew = true;
    this.isEditing = true;
    this.templateForm.reset({ isActive: true });
    this.closeBuilderPanels();
  }

  editTemplate(): void {
    if (this.selectedTemplate) {
      this.isEditing = true;
      this.isNew = false;
      this.templateForm.patchValue(this.selectedTemplate);
      this.updateVariableCategoriesForEventType(this.selectedTemplate.eventType);
    }
  }

  cancelEdit(): void {
    this.isEditing = false;
    this.isNew = false;
    this.templateForm.reset();
    this.closeBuilderPanels();
  }

  closeBuilderPanels(): void {
    this.showButtonBuilder = false;
    this.showVariablePanel = false;
    this.resetButtonConfig();
  }

  saveTemplate(): void {
    if (this.templateForm.invalid) {
      return;
    }

    const template: EmailTemplate = this.templateForm.value;
    this.loading = true;

    if (this.isNew) {
      this.notificationAdminService.createTemplate(template).subscribe({
        next: (created) => {
          this.templates.push(created);
          this.selectedTemplate = created;
          this.isEditing = false;
          this.isNew = false;
          this.loading = false;
          this.showNotification('Template created successfully', 'success');
        },
        error: (err) => {
          this.showNotification('Error creating template', 'error');
          this.loading = false;
        }
      });
    } else {
      this.notificationAdminService.updateTemplate(template.id!, template).subscribe({
        next: (updated) => {
          const index = this.templates.findIndex(t => t.id === updated.id);
          if (index >= 0) {
            this.templates[index] = updated;
          }
          this.selectedTemplate = updated;
          this.isEditing = false;
          this.loading = false;
          this.showNotification('Template updated successfully', 'success');
        },
        error: (err) => {
          this.showNotification('Error updating template', 'error');
          this.loading = false;
        }
      });
    }
  }

  deleteTemplate(): void {
    if (!this.selectedTemplate) return;

    const dialogRef = this.dialogService.open({
      title: 'Confirm Delete',
      content: `Are you sure you want to delete the template "${this.selectedTemplate.name}"?`,
      actions: [
        { text: 'Cancel' },
        { text: 'Delete', themeColor: 'error' }
      ],
      width: 400
    });

    dialogRef.result.subscribe((result: any) => {
      if (result.text === 'Delete' && this.selectedTemplate) {
        this.notificationAdminService.deleteTemplate(this.selectedTemplate.id!).subscribe({
          next: () => {
            this.templates = this.templates.filter(t => t.id !== this.selectedTemplate!.id);
            this.selectedTemplate = null;
            this.showNotification('Template deleted successfully', 'success');
          },
          error: (err) => {
            this.showNotification('Error deleting template', 'error');
          }
        });
      }
    });
  }

  // Variable insertion methods
  toggleVariablePanel(): void {
    this.showVariablePanel = !this.showVariablePanel;
    if (this.showVariablePanel) {
      this.showButtonBuilder = false;
      const eventType = this.templateForm.get('eventType')?.value;
      this.updateVariableCategoriesForEventType(eventType);
    }
  }

  insertVariable(variable: string): void {
    const variableText = `{{${variable}}}`;
    this.insertIntoEditor(variableText);
  }

  insertVariableIntoSubject(variable: string): void {
    const currentSubject = this.templateForm.get('subject')?.value || '';
    this.templateForm.patchValue({
      subject: currentSubject + `{{${variable}}}`
    });
  }

  insertVariableIntoButtonUrl(variable: string): void {
    this.buttonConfig.url += `{{${variable}}}`;
  }

  toggleCategory(category: VariableCategory): void {
    category.expanded = !category.expanded;
  }

  // Button builder methods
  toggleButtonBuilder(): void {
    this.showButtonBuilder = !this.showButtonBuilder;
    if (this.showButtonBuilder) {
      this.showVariablePanel = false;
      this.resetButtonConfig();
    }
  }

  resetButtonConfig(): void {
    this.buttonConfig = {
      text: '',
      url: '',
      style: 'primary'
    };
  }

  getButtonStyleColor(style: string): string {
    const found = this.buttonStyles.find(s => s.value === style);
    return found?.color || '#1d4ed8';
  }

  insertButton(): void {
    if (!this.buttonConfig.text || !this.buttonConfig.url) {
      this.showNotification('Please enter button text and URL', 'warning');
      return;
    }

    const buttonHtml = this.generateButtonHtml(this.buttonConfig);
    this.insertIntoEditor(buttonHtml);
    this.resetButtonConfig();
    this.showButtonBuilder = false;
  }

  generateButtonHtml(config: ButtonConfig): string {
    const colorMap: { [key: string]: { bg: string; hover: string } } = {
      primary: { bg: '#1d4ed8', hover: '#1e40af' },
      secondary: { bg: '#475569', hover: '#334155' },
      success: { bg: '#0f766e', hover: '#115e59' },
      warning: { bg: '#b45309', hover: '#92400e' },
      danger: { bg: '#b42318', hover: '#991b1b' }
    };

    const colors = colorMap[config.style] || colorMap['primary'];

    // Generate an email-safe button using a table-based approach for maximum compatibility
    return `
<table cellpadding="0" cellspacing="0" border="0" style="margin: 16px 0;">
  <tr>
    <td align="center" bgcolor="${colors.bg}" style="border-radius: 8px;">
      <a href="${config.url}" target="_blank" style="
        display: inline-block;
        padding: 14px 28px;
        font-family: 'Segoe UI', Arial, sans-serif;
        font-size: 14px;
        font-weight: 600;
        color: #ffffff;
        text-decoration: none;
        border-radius: 8px;
        background-color: ${colors.bg};
      ">${config.text}</a>
    </td>
  </tr>
</table>`;
  }

  // Insert content into the Kendo editor
  insertIntoEditor(content: string): void {
    const currentBody = this.templateForm.get('body')?.value || '';
    this.templateForm.patchValue({
      body: currentBody + content
    });
  }

  // Quick insert snippets
  insertDivider(): void {
    const dividerHtml = `
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin: 24px 0;">
  <tr>
    <td style="border-top: 1px solid #e2e8f0;"></td>
  </tr>
</table>`;
    this.insertIntoEditor(dividerHtml);
  }

  insertSpacer(): void {
    this.insertIntoEditor('<div style="height: 24px;"></div>');
  }

  insertHeading(): void {
    this.insertIntoEditor('<h2 style="color: #111827; font-family: \'Segoe UI\', Arial, sans-serif; font-size: 20px; font-weight: 600; margin: 24px 0 12px;">Heading Text</h2>');
  }

  insertParagraph(): void {
    this.insertIntoEditor('<p style="color: #374151; font-family: \'Segoe UI\', Arial, sans-serif; font-size: 14px; line-height: 1.6; margin: 0 0 16px;">Your paragraph text here.</p>');
  }

  insertInfoBox(): void {
    const infoBoxHtml = `
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin: 16px 0;">
  <tr>
    <td style="background-color: #e0f2fe; border-left: 4px solid #0284c7; padding: 16px; border-radius: 0 8px 8px 0;">
      <p style="color: #0c4a6e; font-family: 'Segoe UI', Arial, sans-serif; font-size: 14px; line-height: 1.5; margin: 0;">
        <strong>Info:</strong> Your information message here.
      </p>
    </td>
  </tr>
</table>`;
    this.insertIntoEditor(infoBoxHtml);
  }

  insertWarningBox(): void {
    const warningBoxHtml = `
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin: 16px 0;">
  <tr>
    <td style="background-color: #fef3c7; border-left: 4px solid #f59e0b; padding: 16px; border-radius: 0 8px 8px 0;">
      <p style="color: #92400e; font-family: 'Segoe UI', Arial, sans-serif; font-size: 14px; line-height: 1.5; margin: 0;">
        <strong>Warning:</strong> Your warning message here.
      </p>
    </td>
  </tr>
</table>`;
    this.insertIntoEditor(warningBoxHtml);
  }

  onEventTypeChange(): void {
    const eventType = this.templateForm.get('eventType')?.value;
    this.updateVariableCategoriesForEventType(eventType);
  }

  previewTemplate(): void {
    if (!this.selectedTemplate && !this.isEditing) return;

    const template = this.isEditing ? this.templateForm.value : this.selectedTemplate;
    const variables = this.getSampleVariables(template.eventType);

    this.notificationAdminService.previewTemplate(
      this.isEditing && !this.isNew ? template.id : this.selectedTemplate?.id,
      variables
    ).subscribe({
      next: (response) => {
        this.previewHtml = response.html;
        this.showPreview = true;
      },
      error: (err) => {
        // For new templates, just do local replacement
        let preview = template.body;
        Object.keys(variables).forEach(key => {
          preview = preview.replace(new RegExp(`{{${key}}}`, 'g'), variables[key]);
        });
        this.previewHtml = preview;
        this.showPreview = true;
      }
    });
  }

  sendTestEmail(): void {
    if (!this.selectedTemplate || !this.testEmailAddress) return;

    const variables = this.getSampleVariables(this.selectedTemplate.eventType);

    this.notificationAdminService.sendTestEmail(this.selectedTemplate.id!, {
      recipientEmail: this.testEmailAddress,
      variables
    }).subscribe({
      next: () => {
        this.showNotification('Test email sent successfully', 'success');
        this.testEmailAddress = '';
      },
      error: (err) => {
        this.showNotification('Error sending test email', 'error');
      }
    });
  }

  getVariablesForEventType(eventType: string): string[] {
    return [...(this.availableVariables['Common'] || []), ...(this.availableVariables[eventType] || [])];
  }

  private getSampleVariables(eventType: string): { [key: string]: string } {
    const variables: { [key: string]: string } = {
      UserName: 'John Doe',
      UserEmail: 'john.doe@halliburton.com',
      CurrentDate: new Date().toLocaleDateString(),
      AppUrl: 'https://rta.halliburton.com',
      OrderId: '12345',
      OrderType: 'Fixed Cutter',
      Reference: 'FC-2024-001',
      Priority: 'High',
      Requestor: 'Jane Smith',
      RequiredDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString(),
      SubmittedBy: 'Jane Smith',
      SubmittedDate: new Date().toLocaleDateString(),
      ApprovedBy: 'Manager User',
      ApprovalDate: new Date().toLocaleDateString(),
      Comments: 'Sample comments',
      TaskId: '67890',
      TaskName: 'Design Review',
      AssignedTo: 'Engineer User',
      DueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString(),
      PickedUpBy: 'Engineer User',
      PickupTime: new Date().toLocaleString(),
      CutterFamily: 'PDC-Premium',
      Justification: 'Required for high-temperature application',
      ApproveUrl: 'https://rta.halliburton.com/approve/123',
      DenyUrl: 'https://rta.halliburton.com/deny/123',
      ApprovalCode: 'OVRD-2024-001',
      ExpirationDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toLocaleDateString()
    };
    return variables;
  }

  private showNotification(message: string, type: 'success' | 'error' | 'warning' | 'info'): void {
    this.notificationService.show({
      content: message,
      type: { style: type, icon: true },
      position: { horizontal: 'right', vertical: 'top' },
      animation: { type: 'fade', duration: 400 },
      hideAfter: 3000
    });
  }
}
